import React, {
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
} from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import "./style.css";
import pii from "../assets/Blogs/pi.webp";
import rightarrow from "../assets/Blogs/rightarrow.webp";
import Popup from "../components/popup.js";
import { Helmet } from "react-helmet-async";
import useFetch from "../hooks/useFetch.js";
import Lottie from "react-lottie";
import loading from "../pages/loading.json";
import Sidebar, { SearchContext } from "../pages/SidebarAcademy.js";
import Header from "../pages/HeaderAcademy.js";
import BookaCall from "../pages/BookaCall";
import DownloadBrochure from "../pages/DownloadBrochure";
import aiCourseBanner from "../assets/Academy/Corporate.webp";

function titleToSlug(title) {
  return title
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const Corporatecourse = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isBookCallOpen, setIsBookCallOpen] = useState(false);
  const [isDownloadBrochureOpen, setIsDownloadBrochureOpen] = useState(false);
  const [selectedAcademyBrochure, setSelectedAcademyBrochure] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFromSearch, setIsFromSearch] = useState(false); // Track if navigation came from search

  // Fetch academy data
  const {
    loading: academyLoading,
    data: academyData,
    error: academyError,
  } = useFetch(
    `https://devrw2.blob.core.windows.net/rw-website/strapi-data/academy/data.json`
  );

  // Get search term from URL params if navigated with search
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get("search");
    const fromSearch = urlParams.get("fromSearch"); // Add this parameter when navigating from search

    if (searchParam) {
      setSearchTerm(searchParam);
      setIsFromSearch(fromSearch === "true");
    }
  }, [location.search]);

  // Update URL when search term changes (optional - for bookmarkable searches)
  useEffect(() => {
    if (searchTerm.trim()) {
      const newUrl = `${location.pathname}?search=${encodeURIComponent(
        searchTerm
      )}${isFromSearch ? "&fromSearch=true" : ""}`;
      window.history.replaceState(null, "", newUrl);
    } else {
      window.history.replaceState(null, "", location.pathname);
      setIsFromSearch(false);
    }
  }, [searchTerm, location.pathname, isFromSearch]);

  // Function to handle the Start Now button click
  const handleStartNowClick = () => {
    // Navigate to the AI Readiness Index page
    navigate("/ai-readiness-index");

    // Use setTimeout to ensure navigation happens before trying to scroll
    setTimeout(() => {
      const businessSection = document.getElementById("business-section");
      if (businessSection) {
        businessSection.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: loading,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  if (academyLoading) {
    return (
      <div className="w-full flex justify-center items-center min-h-screen">
        <Lottie options={defaultOptions} height={400} width={400} />
      </div>
    );
  }

  const academyItems = Array.isArray(academyData) ? academyData : [];

  // Enhanced filtering logic (replicated from Individual course)
  const filteredCourses = academyItems.filter((academy) => {
    // First filter by category
    if (academy.academyCategory !== "For Corporates") return false;

    // If no search term, show all courses in this category
    if (!searchTerm.trim()) return true;

    // If navigation came from search (Enter key press), show only exact match
    if (isFromSearch && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      return academy.academyTitle?.toLowerCase().includes(searchLower);
    }

    // For regular search (typing in sidebar), show all matching courses
    const searchLower = searchTerm.toLowerCase();
    return (
      academy.academyTitle?.toLowerCase().includes(searchLower) ||
      academy.academyDesc?.toLowerCase().includes(searchLower) ||
      academy.academyCategory?.toLowerCase().includes(searchLower)
    );
  });

  // If from search, show only the best match (first match)
  const coursesToShow =
    isFromSearch && filteredCourses.length > 0
      ? [filteredCourses[0]]
      : filteredCourses;

  const handleClearSearch = () => {
    setSearchTerm("");
    setIsFromSearch(false);
    // Update URL to remove search params
    window.history.replaceState(null, "", location.pathname);
  };

  const BlogCard = ({ academy }) => {
    const titleRef = useRef();
    const [titleLines, setTitleLines] = useState(1);
    const { searchTerm } = useContext(SearchContext);

    useEffect(() => {
      if (titleRef.current) {
        const lineHeight = parseInt(
          window.getComputedStyle(titleRef.current).lineHeight
        );
        const titleHeight = titleRef.current.clientHeight;
        const lines = Math.ceil(titleHeight / lineHeight);
        setTitleLines(lines);
      }
    }, [titleRef.current]);

    const handleDownloadBrochure = (academy) => {
      // Set the selected academy details for the download brochure modal
      setSelectedAcademyBrochure(academy);
      setIsDownloadBrochureOpen(true);
    };

    // Function to highlight search terms
    const highlightText = (text, searchTerm) => {
      if (!searchTerm || !text) return text;

      const regex = new RegExp(`(${searchTerm})`, "gi");
      const parts = text.split(regex);

      return parts.map((part, index) =>
        regex.test(part) ? (
          <span key={index} className="bg-yellow-200 font-medium">
            {part}
          </span>
        ) : (
          part
        )
      );
    };

    const descriptionLines = 7 - titleLines;

    return (
      <div className="bg-[#ECEFF8] overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow duration-300 flex flex-col h-full rounded-3xl">
        <div className="relative w-full h-48 overflow-hidden">
          <img
            className="object-cover w-full h-full"
            src={academy.academycoverImg.url}
            alt={academy.academyTitle}
          />
        </div>
        <div className="p-4 flex flex-col flex-grow">
          <h3 ref={titleRef} className="text-lg font-bold text-gray-800 mb-2">
            {highlightText(academy.academyTitle, searchTerm)}
          </h3>
          <p
            className="text-gray-600 text-sm mb-4"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: descriptionLines,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {highlightText(academy.academyDesc, searchTerm)}
          </p>
          <div className="flex flex-col gap-2 mt-auto">
            <button
              onClick={() => setIsBookCallOpen(true)}
              className="bg-[rgba(62,89,183,1)] text-white px-24 md:px-10 lg:px-20 py-2 rounded-lg hover:bg-blue-700 transition text-nowrap"
            >
              Book a Call
            </button>
            <button
              onClick={() => handleDownloadBrochure(academy)}
              className="border-2 border-[rgba(62,89,183,1)] text-[rgba(62,89,183,1)] px-6 md:px-4 lg:px-6 py-2 rounded-lg hover:bg-[rgba(62,89,183,1)] hover:text-white transition text-center text-nowrap"
            >
              Download Brochure
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <SearchContext.Provider value={{ searchTerm, setSearchTerm }}>
      <div className="bg-gray-50 font-display">
        <Header />

        <div className="px-4 py-8 flex flex-col md:flex-row">
          <Sidebar searchContextValue={{ searchTerm, setSearchTerm }} />
          <div className="md:w-4/5 2xl:w-full">
            <div className="p-4 mb-8 ">
              <div className="mb-2">
                <div className="mb-6 relative overflow-hidden">
                  <img
                    src={aiCourseBanner}
                    alt="AI Literacy Assessment"
                    className="w-full h-24 md:h-44 lg:h-full"
                  />
                  <div className="absolute inset-0 flex items-center">
                    <div className="flex flex-row w-full justify-between items-center px-4 md:px-6 lg:px-16 2xl:px-32">
                      <div className="max-w-lg lg:max-w-md 2xl:max-w-screen-sm">
                        <h2 className="text-xs md:text-lg lg:text-2xl 2xl:text-4xl font-bold text-white mb-2 text-left">
                          Is Your Organization AI-Ready?
                        </h2>
                        <p className="text-xs md:text-sm lg:text-lg 2xl:text-2xl text-white">
                          Take the assessment and get your full readiness report
                          in just 15 minutes.
                        </p>
                      </div>
                      <div className="ml-4">
                        <button
                          onClick={handleStartNowClick}
                          className="inline-flex items-center text-[8px] md:text-md lg:text-lg 2xl:text-2xl bg-white text-black font-medium px-4 md:px-6 md:py-2 rounded-full hover:bg-gray-100 transition"
                        >
                          Start Now
                          <span className="ml-2">→</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-left text-black">
                  Artifical Intelligence Courses (12)
                </h2>
              </div>
              <div className="mb-4 flex justify-between items-center">
                <h2 className="text-lg font-medium text-left text-gray-900">
                  For Corporates ({coursesToShow.length})
                </h2>
              </div>

              {coursesToShow.length === 0 && searchTerm ? (
                <div className="text-center py-12">
                  <div className="text-gray-500 text-lg mb-2">
                    No courses found
                  </div>
                  <div className="text-gray-400 text-sm">
                    Try adjusting your search terms or{" "}
                    <button
                      onClick={handleClearSearch}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      view all courses
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                  {coursesToShow.map((academy) => (
                    <BlogCard key={academy.id} academy={academy} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <BookaCall
          isOpen={isBookCallOpen}
          onClose={() => setIsBookCallOpen(false)}
        />

        {selectedAcademyBrochure && (
          <DownloadBrochure
            isOpen={isDownloadBrochureOpen}
            onClose={() => setIsDownloadBrochureOpen(false)}
            utmContent="corporate-courses"
            academyTitle={selectedAcademyBrochure.academyTitle || ""}
            brochureUrl={
              selectedAcademyBrochure.academyDownloadBrochure &&
              selectedAcademyBrochure.academyDownloadBrochure.length > 0
                ? selectedAcademyBrochure.academyDownloadBrochure[0].url
                : ""
            }
            academyBrochureImage={selectedAcademyBrochure.academyBrochureImage}
          />
        )}

        <section className="mt-20">
          <Popup utmContent={"blog"} />
        </section>
      </div>
    </SearchContext.Provider>
  );
};

// Export for compatibility if needed
export { SearchContext };
export default Corporatecourse;
